/**
 * Reporte: Recaudación por período (NUEVO M13) — Fase 7.3 (reporte 4 de 7)
 *
 * Serie mensual de los últimos 12 meses con totales de emisión y
 * acreditación. Reutiliza `vw_recaudacion_mensual` (decisión #032).
 *
 * Nota: el desglose por vía de pago (CVU vs ventanilla vs transferencia)
 * no está en la vista actual. Si ATSA lo necesita, sumamos una migration
 * con una vista nueva. Por ahora mostramos el agregado total.
 */

import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { useRecaudacionMensual, type RecaudacionMesRow } from '../../hooks/useDashboard';
import { formatCurrency, formatPeriodo } from '../../utils/format';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtCurrency,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Tipos auxiliares ──────────────────────────────────────────────────────────

interface RowReporte extends RecaudacionMesRow {
  /** Saldo no acreditado del mes (emitido - acreditado) */
  saldo_mes: number;
  /** % de acreditación sobre emitido */
  porcentaje_acreditacion: number;
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function RecaudacionPorPeriodoReporte() {
  const { data: serie = [], isLoading, isError } = useRecaudacionMensual();

  // Ordenamos del más reciente al más viejo (la vista devuelve cronológica)
  const rows: RowReporte[] = useMemo(() => {
    return [...serie]
      .sort((a, b) => b.mes.localeCompare(a.mes))
      .map((r) => ({
        ...r,
        saldo_mes: Number(r.monto_emitido) - Number(r.monto_acreditado),
        porcentaje_acreditacion:
          Number(r.monto_emitido) > 0
            ? (Number(r.monto_acreditado) / Number(r.monto_emitido)) * 100
            : 0,
      }));
  }, [serie]);

  const totales = useMemo(
    () => ({
      meses: rows.length,
      totalEmitido: rows.reduce((s, r) => s + Number(r.monto_emitido), 0),
      totalAcreditado: rows.reduce((s, r) => s + Number(r.monto_acreditado), 0),
      totalCupones: rows.reduce((s, r) => s + r.cant_cupones, 0),
      totalPagos: rows.reduce((s, r) => s + r.cant_pagos, 0),
    }),
    [rows],
  );

  const porcentajeGlobal =
    totales.totalEmitido > 0
      ? (totales.totalAcreditado / totales.totalEmitido) * 100
      : 0;

  const subtitulo = `Últimos ${rows.length} meses · Acreditación global: ${porcentajeGlobal.toFixed(1)}%`;

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Recaudación por período',
      subtitulo,
      orientacion: 'landscape',
    });

    const columnas: ColumnaPdf[] = [
      { header: 'Período', align: 'center', format: (v) => formatPeriodo(String(v)) },
      { header: 'Cupones', align: 'right', format: fmtText },
      { header: 'Pagos', align: 'right', format: fmtText },
      { header: 'Emitido', align: 'right', format: fmtCurrency },
      { header: 'Acreditado', align: 'right', format: fmtCurrency },
      { header: 'Saldo', align: 'right', format: fmtCurrency },
      { header: '% acred.', align: 'right', format: (v) => `${Number(v).toFixed(1)}%` },
    ];

    const body: unknown[][] = rows.map((r) => [
      r.mes,
      r.cant_cupones,
      r.cant_pagos,
      r.monto_emitido,
      r.monto_acreditado,
      r.saldo_mes,
      r.porcentaje_acreditacion,
    ]);

    body.push([
      `TOTAL (${totales.meses} meses)`,
      totales.totalCupones,
      totales.totalPagos,
      totales.totalEmitido,
      totales.totalAcreditado,
      totales.totalEmitido - totales.totalAcreditado,
      porcentajeGlobal,
    ]);

    addReporteTable(doc, columnas, body, {
      titulo: 'Recaudación por período',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    triggerBlobDownload(blob, buildReporteFilename('recaudacion-por-periodo', 'pdf'));
  };

  // ── Export Excel ───────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const columnas: ColumnaExcel[] = [
      { header: 'Período', key: 'periodo', width: 14, format: 'text' },
      { header: 'Cupones emitidos', key: 'cupones', width: 16, format: 'integer' },
      { header: 'Pagos acreditados', key: 'pagos', width: 16, format: 'integer' },
      { header: 'Monto emitido', key: 'emitido', width: 16, format: 'currency' },
      { header: 'Monto acreditado', key: 'acreditado', width: 16, format: 'currency' },
      { header: 'Saldo mes', key: 'saldo', width: 16, format: 'currency' },
      { header: '% acreditación', key: 'porcentaje', width: 14, format: 'text' },
    ];

    const rowsExcel = rows.map((r) => ({
      periodo: formatPeriodo(r.mes),
      cupones: r.cant_cupones,
      pagos: r.cant_pagos,
      emitido: r.monto_emitido,
      acreditado: r.monto_acreditado,
      saldo: r.saldo_mes,
      porcentaje: `${r.porcentaje_acreditacion.toFixed(1)}%`,
    }));

    const blob = await generarReporteExcel({
      titulo: 'Recaudación por período',
      subtitulo,
      nombreHoja: 'Recaudación',
      columnas,
      rows: rowsExcel,
    });
    triggerBlobDownload(blob, buildReporteFilename('recaudacion-por-periodo', 'xlsx'));
  };

  // ── DataGrid ────────────────────────────────────────────────────────────
  const columns: GridColDef<RowReporte>[] = [
    {
      field: 'mes',
      headerName: 'Período',
      width: 150,
      renderCell: (p: GridRenderCellParams<RowReporte>) => (
        <Typography variant="body2" fontWeight={500}>
          {formatPeriodo(p.row.mes)}
        </Typography>
      ),
    },
    {
      field: 'cant_cupones',
      headerName: 'Cupones',
      width: 110,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'cant_pagos',
      headerName: 'Pagos',
      width: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'monto_emitido',
      headerName: 'Emitido',
      width: 160,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<RowReporte>) =>
        formatCurrency(p.row.monto_emitido),
    },
    {
      field: 'monto_acreditado',
      headerName: 'Acreditado',
      width: 160,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<RowReporte>) => (
        <Typography variant="body2" color="success.main" fontWeight={500}>
          {formatCurrency(p.row.monto_acreditado)}
        </Typography>
      ),
    },
    {
      field: 'saldo_mes',
      headerName: 'Saldo',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<RowReporte>) => (
        <Typography
          variant="body2"
          color={p.row.saldo_mes > 0 ? 'warning.main' : 'inherit'}
        >
          {formatCurrency(p.row.saldo_mes)}
        </Typography>
      ),
    },
    {
      field: 'porcentaje_acreditacion',
      headerName: '% acreditación',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<RowReporte>) => (
        <Typography
          variant="body2"
          fontWeight={500}
          color={
            p.row.porcentaje_acreditacion >= 80
              ? 'success.main'
              : p.row.porcentaje_acreditacion >= 50
                ? 'warning.main'
                : 'error.main'
          }
        >
          {p.row.porcentaje_acreditacion.toFixed(1)}%
        </Typography>
      ),
    },
  ];

  const sinDatos = !isLoading && !isError && rows.length === 0;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <TrendingUpIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Recaudación por período
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Serie mensual de los últimos 12 meses con cupones emitidos vs
            acreditados.
          </Typography>
        </Box>
      </Stack>

      {/* Totales + Export */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Meses
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.meses}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total emitido
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(totales.totalEmitido)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total acreditado
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {formatCurrency(totales.totalAcreditado)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              % global
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color={
                porcentajeGlobal >= 80
                  ? 'success.main'
                  : porcentajeGlobal >= 50
                    ? 'warning.main'
                    : 'error.main'
              }
            >
              {porcentajeGlobal.toFixed(1)}%
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={rows.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={rows.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar la serie de recaudación.
        </Alert>
      )}
      {sinDatos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sin datos de recaudación para mostrar.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.mes}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[15, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          sx={{ border: 0 }}
        />
      </Card>
    </Box>
  );
}
